import React, { useState, useEffect } from 'react'
import {
  BookOpen, ChevronRight, CheckCircle, Lightbulb,
  Play, Target, Brain, ArrowRight, Star, AlertTriangle,
  Calculator, BarChart2, FileText, Layers, PenTool, Lock,
  Youtube, Hash, Divide, Grid, Zap, TrendingUp, Flame, ChevronDown, X,
  MessageCircle, Sparkles
} from 'lucide-react'
import { useProgressStore, useLearnProgress, useUnitProgress } from '../store/progressStore'
import { useVideoStore } from '../store/videoStore'
import ConceptVideoModal from '../components/ConceptVideoModal'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG,
  type PredictedFragment
} from '../store/predictedFragments'

// ── 스토리텔링 인트로 데이터 ─────────────────────────────────────────────────
interface StoryLine {
  speaker: 'student' | 'teacher' | 'friend' | 'narrator'
  emoji: string
  name: string
  text: string
}
interface StepStory {
  scene: string          // 배경 묘사
  lines: StoryLine[]     // 대화
  reveal: string         // 수학 연결 한 줄 요약
  revealEmoji: string
}

const STEP_STORIES: Record<string, StepStory> = {
  NS01: {
    scene: '🏫 중학교 수학 수업 첫날. 선생님이 칠판에 숫자들을 쓴다.',
    lines: [
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '여러분, 이 숫자들 중에서 "혼자 사는 수"와 "친구가 있는 수"로 나눠볼게요.' },
      { speaker: 'student', emoji: '🙋', name: '민준', text: '혼자 사는 수요? 그게 무슨 말이에요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '7을 볼까요? 7은 1하고 자기 자신(7)으로만 나눠져요. 친구가 없는 외로운 수죠. 이런 수를 소수라고 해요!' },
      { speaker: 'friend', emoji: '😄', name: '소연', text: '그럼 6은요? 6은 1, 2, 3, 6으로 나눠지니까 친구가 많은 합성수겠네요!' },
      { speaker: 'student', emoji: '😮', name: '민준', text: '아! 약수가 딱 2개면 소수, 3개 이상이면 합성수! 근데... 1은요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '좋은 질문! 1은 약수가 1개뿐이라 소수도 합성수도 아닌 특별한 수예요. ✨' },
    ],
    reveal: '소수 = 약수가 딱 2개 (1과 자기자신) | 합성수 = 약수가 3개 이상',
    revealEmoji: '🔢',
  },
  NS02: {
    scene: '📱 쉬는 시간, 민준이가 스마트폰을 보다가 궁금한 게 생겼다.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '민준', text: '소연아, 게임에서 "공격력 2³"이라고 나왔는데 이게 6이야 8이야?' },
      { speaker: 'friend', emoji: '😄', name: '소연', text: '6이면 2×3이고, 8이면 2×2×2야. 뭐가 맞을 것 같아?' },
      { speaker: 'student', emoji: '🤔', name: '민준', text: '음... 2³이니까 2×3=6 아닌가?' },
      { speaker: 'friend', emoji: '😅', name: '소연', text: '많이들 헷갈려! 지수는 "몇 번 곱하는지"야. 2³ = 2×2×2 = 8이지, 2×3이 아니야!' },
      { speaker: 'student', emoji: '😯', name: '민준', text: '아!! 지수는 곱하는 횟수구나! 그러면 2⁴는 2×2×2×2=16이겠다!' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '소인수분해에서 2×2×3×3을 2²×3²으로 쓸 수 있는 이유가 바로 거듭제곱 때문이에요.' },
    ],
    reveal: '2³ = 2×2×2 = 8 (2를 3번 곱함) ≠ 2×3 = 6',
    revealEmoji: '⬆️',
  },
  NS03: {
    scene: '🍕 점심시간. 피자 72조각을 가장 작은 단위로 쪼개는 상황.',
    lines: [
      { speaker: 'friend', emoji: '😄', name: '소연', text: '민준아, 피자 72조각을 더 이상 못 나눌 때까지 쪼개면 어떻게 될까?' },
      { speaker: 'student', emoji: '🤔', name: '민준', text: '72를 2로 나누면 36, 또 2로 나누면 18, 또 나누면 9... 이제 3으로 나눠서 3, 또 3으로 나누면 1!' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '완벽해요! 72 = 2×2×2×3×3 = 2³×3². 이렇게 소수만 남을 때까지 나누는 게 소인수분해예요!' },
      { speaker: 'student', emoji: '😮', name: '민준', text: '그러면 어떤 방법으로 해도 결과가 항상 같아요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '맞아요! 그걸 "소인수분해의 유일성"이라 해요. 어떤 방법을 써도 72는 반드시 2³×3²이 돼요. 수학의 신기한 점이죠!' },
    ],
    reveal: '72 = 2|72 → 2|36 → 2|18 → 3|9 → 3|3 → 1 ∴ 72 = 2³×3²',
    revealEmoji: '🌳',
  },
  NS04: {
    scene: '🎨 미술 시간. 가로 24cm, 세로 36cm 도화지를 정사각형으로 자르려 한다.',
    lines: [
      { speaker: 'friend', emoji: '😄', name: '소연', text: '선생님, 이 도화지를 낭비 없이 같은 크기 정사각형으로 자르고 싶은데요. 제일 큰 크기는 몇 cm예요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '24랑 36의 최대공약수를 구하면 돼요! 24=2³×3, 36=2²×3²... 공통 소인수의 낮은 지수를 뽑으면?' },
      { speaker: 'student', emoji: '🙋', name: '민준', text: '2는 min(3,2)=2, 3은 min(1,2)=1이니까 2²×3=12! 12cm 정사각형이요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '정확해요! 24÷12=2장, 36÷12=3장. 총 6장의 12cm 정사각형 타일이 나와요.' },
      { speaker: 'friend', emoji: '😮', name: '소연', text: '와, GCD가 이렇게 실생활에 쓰이는 거였구나! 최대한 크게 = GCD!' },
    ],
    reveal: 'GCD = 공통 소인수 × 낮은 지수 선택 | "최대한 크게 나누기" 문제에 사용',
    revealEmoji: '🤝',
  },
  NS05: {
    scene: '🚌 학교 앞 버스 정류장. 민준이와 소연이가 버스를 기다리고 있다.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '민준', text: '소연아, A버스는 6분마다, B버스는 9분마다 와. 지금 둘 다 동시에 떠났는데, 언제 또 같이 올까?' },
      { speaker: 'friend', emoji: '🤔', name: '소연', text: '음... 6의 배수: 6, 12, 18... 9의 배수: 9, 18, 27... 18분 후에 같이 오네!' },
      { speaker: 'student', emoji: '😄', name: '민준', text: '그러면 이게 LCM이구나! 두 수의 공배수 중 가장 작은 수!' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '맞아요! 6=2×3, 9=3² → LCM=2×3²=18. "언제 다시 만나는가"가 LCM이에요.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: 'GCD는 "나누기(쪼개기)" 문제, LCM은 "만나기(겹치기)" 문제! 이 구분이 핵심이에요.' },
    ],
    reveal: 'LCM = 모든 소인수 × 큰 지수 선택 | "언제 다시 만나는가" 문제에 사용',
    revealEmoji: '🔄',
  },
  NS06: {
    scene: '🏙️ 교통 신호등 앞. 선생님이 실생활 문제를 낸다.',
    lines: [
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '퀴즈! 신호등 A는 4분, B는 6분마다 초록불이 켜진다. 동시에 켜진 후 다음번엔 몇 분 후에 같이 켜질까?' },
      { speaker: 'student', emoji: '🙋', name: '민준', text: '만나는 문제니까 LCM! LCM(4,6) = 2²×3 = 12분 후요!' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '완벽! 그럼 사탕 24개 초콜릿 36개를 최대한 많은 친구에게 똑같이 나눠주려면?'},
      { speaker: 'friend', emoji: '😄', name: '소연', text: '나누는 문제니까 GCD! GCD(24,36)=12. 12명에게 사탕 2개, 초콜릿 3개씩이요!' },
      { speaker: 'student', emoji: '💪', name: '민준', text: '"나누기" → GCD, "만나기" → LCM. 이제 문장만 읽어도 바로 구분할 수 있어!' },
    ],
    reveal: '"나누기/쪼개기" → GCD | "만나기/겹치기" → LCM | 문제 상황 판단이 핵심',
    revealEmoji: '🧩',
  },
  NS07: {
    scene: '📝 시험 직전. 민준이가 오답 노트를 펼쳐 마지막 점검을 한다.',
    lines: [
      { speaker: 'student', emoji: '😰', name: '민준', text: '아, 저번 시험에서 GCD 구할 때 지수 큰 걸 골라서 틀렸어... 이번엔 절대 안 틀리는데.' },
      { speaker: 'friend', emoji: '😄', name: '소연', text: '외우기 쉬운 방법 가르쳐줄게. GCD는 "교집합"이니까 작은 지수, LCM은 "합집합"이니까 큰 지수!' },
      { speaker: 'student', emoji: '🤔', name: '민준', text: '그리고 1은 소수가 아니라는 것, 2³은 2×3이 아니라 8이라는 것... 다 기억해야 해.' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '여러분, 오개념 12가지를 한번 더 훑어봐요. 이걸 알고 있으면 시험에서 절반 이상은 막아낼 수 있어요!' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '오개념 점검은 틀리는 패턴을 없애는 가장 빠른 방법이에요. 틀린 이유를 알면 다시는 안 틀려요!' },
    ],
    reveal: 'GCD=낮은 지수(교집합) | LCM=높은 지수(합집합) | GCD×LCM = a×b',
    revealEmoji: '🔍',
  },
  NS08: {
    scene: '😓 민준이의 방. 수학 교과서를 보며 한숨을 쉬고 있다.',
    lines: [
      { speaker: 'student', emoji: '😓', name: '민준', text: '소인수분해... 소수, 합성수, 지수, GCD, LCM... 이걸 다 언제 외워. 나 수학 포기할래.' },
      { speaker: 'friend', emoji: '😄', name: '소연', text: '잠깐! 나도 처음엔 그랬어. 그런데 사실 나눗셈만 할 줄 알면 돼. 12를 2로 나눠봐.' },
      { speaker: 'student', emoji: '🤔', name: '민준', text: '12÷2=6... 6÷2=3... 3÷3=1. 이게 끝이야?' },
      { speaker: 'friend', emoji: '😁', name: '소연', text: '맞아! 12 = 2×2×3 = 2²×3. 소인수분해 완성! 나눗셈만 했잖아.' },
      { speaker: 'student', emoji: '😮', name: '민준', text: '설마... 이게 진짜 소인수분해야?! 이렇게 쉬웠어??'},
      { speaker: 'narrator', emoji: '🌱', name: '', text: '소인수분해의 90%는 나눗셈입니다. 천천히 작은 소수(2,3,5,7...)부터 시작하면 누구나 할 수 있어요!' },
    ],
    reveal: '나눗셈만 알면 소인수분해 OK! 2→3→5→7 순서로 나누면 끝',
    revealEmoji: '🌱',
  },
}

// ── 소인수분해 8단계 학습 데이터 ─────────────────────────────────────────────
const STEPS = [
  {
    id: 'NS01',
    order: 1,
    title: '소수와 합성수',
    subtitle: '자연수를 소수와 합성수로 분류한다',
    difficulty: 'beginner' as const,
    teaches: ['Prime', 'Composite', 'Factor'],
    icon: '🔢',
    color: { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700', accent: 'text-blue-600', btn: 'bg-blue-500' },
    objective: '소수와 합성수의 정의를 이해하고, 100 이하의 소수를 에라토스테네스의 체로 구할 수 있다.',
    concept: `소수(Prime Number)란 1과 자기 자신만을 약수로 가지는 2 이상의 자연수입니다.
합성수(Composite Number)란 소수가 아닌 1 이외의 자연수, 즉 약수가 3개 이상인 수입니다.

📌 핵심 개념
• 1은 소수도 합성수도 아닙니다 (약수가 1뿐)
• 2는 유일한 짝수 소수입니다
• 소수 예: 2, 3, 5, 7, 11, 13, 17, 19, 23, 29 …
• 합성수 예: 4, 6, 8, 9, 10, 12 …

🔍 에라토스테네스의 체
① 1을 지운다
② 2를 남기고, 2의 배수를 모두 지운다
③ 3을 남기고, 3의 배수를 모두 지운다
④ 지워지지 않은 수를 남기고 그 배수를 지운다
→ 남은 수가 모두 소수!

⚠️ 수포자 탈출 포인트
"소수는 외워야 해?"가 아니라 "약수가 딱 2개(1과 자기 자신)인지" 확인하는 방법을 이해하세요!`,
    examples: [
      { q: '다음 중 소수를 모두 고르세요: 1, 7, 9, 11, 15, 17', a: '7, 11, 17', detail: '• 1: 약수 {1} → 소수도 합성수도 아님\n• 7: 약수 {1, 7} → 소수 ✓\n• 9: 약수 {1, 3, 9} → 합성수 (3×3=9)\n• 11: 약수 {1, 11} → 소수 ✓\n• 15: 약수 {1, 3, 5, 15} → 합성수\n• 17: 약수 {1, 17} → 소수 ✓' },
      { q: '51은 소수인가요, 합성수인가요?', a: '합성수 (51 = 3 × 17)', detail: '51이 소수처럼 보이지만, 51 ÷ 3 = 17이므로 약수가 1, 3, 17, 51 → 4개 → 합성수입니다.' },
    ],
    keyPoints: ['1은 소수도 합성수도 아니다', '2는 유일한 짝수 소수', '약수가 딱 2개면 소수'],
    practice: {
      q: '30 이하의 소수를 에라토스테네스의 체 방법으로 모두 구하세요.',
      a: '2, 3, 5, 7, 11, 13, 17, 19, 23, 29',
      hint: '2의 배수, 3의 배수, 5의 배수 순서로 지워나가세요.',
      solutionSteps: [
        { label: '① 1 제거', content: '1은 소수도 합성수도 아님 → 제거' },
        { label: '② 2의 배수 제거', content: '2는 남기고 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30 제거' },
        { label: '③ 3의 배수 제거', content: '3은 남기고 9, 15, 21, 27 제거 (이미 지워진 것 제외)' },
        { label: '④ 5의 배수 제거', content: '5는 남기고 25 제거' },
        { label: '⑤ 결과', content: '남은 수: 2, 3, 5, 7, 11, 13, 17, 19, 23, 29 → 모두 소수!' },
      ]
    },
    misconceptions: [
      { code: 'NM01', desc: '1을 소수로 생각한다 → 1은 약수가 1개뿐이라 소수가 아니다!' },
      { code: 'NM02', desc: '짝수는 모두 합성수라고 생각한다 → 2는 짝수이면서 소수!' },
    ],
  },
  {
    id: 'NS02',
    order: 2,
    title: '거듭제곱과 지수',
    subtitle: '같은 수를 여러 번 곱한 것을 간단히 표현한다',
    difficulty: 'beginner' as const,
    teaches: ['Power', 'Exponent', 'Base'],
    icon: '⬆️',
    color: { bg: 'bg-indigo-50', border: 'border-indigo-200', badge: 'bg-indigo-100 text-indigo-700', accent: 'text-indigo-600', btn: 'bg-indigo-500' },
    objective: '거듭제곱의 밑과 지수를 이해하고, aⁿ 형태로 읽고 쓸 수 있다.',
    concept: `거듭제곱(Power)이란 같은 수를 여러 번 곱한 것을 간단히 나타낸 표현입니다.

📌 핵심 개념
• aⁿ 읽기: "a의 n제곱" 또는 "a를 n번 곱한 것"
  - 밑(base): a (곱해지는 수)
  - 지수(exponent): n (곱하는 횟수)

• 예시
  - 2³ = 2 × 2 × 2 = 8
  - 5² = 5 × 5 = 25
  - 3⁴ = 3 × 3 × 3 × 3 = 81
  - 10¹ = 10
  - a¹ = a (지수가 1이면 그 수 자체)

⚠️ 주의사항
• 2³ ≠ 2 × 3 = 6  (잘못된 계산!)
• 2³ = 2 × 2 × 2 = 8  (올바른 계산!)
  
🔗 소인수분해와의 연결
소인수분해에서 2 × 2 × 3 × 3 = 2² × 3²처럼 거듭제곱을 써서 간단히 나타냅니다.`,
    examples: [
      { q: '2 × 2 × 2 × 2 × 2를 거듭제곱으로 나타내고 계산하세요.', a: '2⁵ = 32', detail: '2가 5번 곱해졌으므로 밑=2, 지수=5\n2⁵ = 2×2×2×2×2 = 4×4×2 = 16×2 = 32' },
      { q: '3² × 2³의 값을 구하세요.', a: '9 × 8 = 72', detail: '3² = 3×3 = 9\n2³ = 2×2×2 = 8\n9 × 8 = 72' },
    ],
    keyPoints: ['aⁿ = a를 n번 곱한 것', '거듭제곱 ≠ 곱셈', '밑과 지수를 구별한다'],
    practice: {
      q: '다음을 계산하세요: ① 3³  ② 2⁴  ③ 5² × 2³',
      a: '① 27  ② 16  ③ 200',
      hint: '지수만큼 밑을 반복해서 곱하세요.',
      solutionSteps: [
        { label: '① 3³ 계산', content: '3 × 3 × 3 = 9 × 3 = 27' },
        { label: '② 2⁴ 계산', content: '2 × 2 × 2 × 2 = 4 × 4 = 16' },
        { label: '③ 5² × 2³ 계산', content: '5² = 25, 2³ = 8 → 25 × 8 = 200' },
      ]
    },
    misconceptions: [
      { code: 'NM03', desc: '2³을 2×3=6으로 계산한다 → 지수는 곱셈 횟수이지 곱할 수가 아님!' },
      { code: 'NM04', desc: '(-2)²과 -2²을 같다고 생각한다 → (-2)²=4이지만 -2²=-(4)=-4' },
    ],
  },
  {
    id: 'NS03',
    order: 3,
    title: '소인수분해 방법',
    subtitle: '자연수를 소수의 곱으로 분해한다',
    difficulty: 'intermediate' as const,
    teaches: ['PrimeFactorization', 'FactorTree', 'LadderMethod'],
    icon: '🌳',
    color: { bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700', accent: 'text-emerald-600', btn: 'bg-emerald-500' },
    objective: '인수분해 나무(factor tree) 또는 나눗셈 사다리 방법으로 자연수를 소인수분해하고, 거듭제곱으로 나타낼 수 있다.',
    concept: `소인수분해(Prime Factorization)란 자연수를 소수의 곱으로 나타내는 것입니다.
소인수(Prime Factor)란 소수인 약수를 말합니다.

📌 방법 1: 인수분해 나무 (Factor Tree)
    72
   /  \\
  8    9
 / \\  / \\
2  4  3  3
  / \\
 2   2
→ 72 = 2³ × 3²

📌 방법 2: 나눗셈 사다리 (Ladder/Division Method)
2 | 72
2 | 36
2 | 18
3 |  9
3 |  3
   1
→ 72 = 2 × 2 × 2 × 3 × 3 = 2³ × 3²

📌 소인수분해의 유일성
어떤 방법으로 인수분해해도 결과는 항상 같습니다!
(소인수분해의 유일성 = 산술의 기본 정리)`,
    examples: [
      { q: '60을 소인수분해 하세요.', a: '60 = 2² × 3 × 5', detail: '2 | 60\n2 | 30\n3 | 15\n5 |  5\n    1\n→ 60 = 2 × 2 × 3 × 5 = 2² × 3 × 5' },
      { q: '180을 소인수분해 하세요.', a: '180 = 2² × 3² × 5', detail: '2 | 180\n2 |  90\n3 |  45\n3 |  15\n5 |   5\n     1\n→ 180 = 2² × 3² × 5' },
    ],
    keyPoints: ['가장 작은 소수부터 나눈다', '몫이 1이 될 때까지 반복', '결과는 거듭제곱으로 표현'],
    practice: {
      q: '다음 수를 소인수분해하여 거듭제곱으로 나타내세요: ① 36  ② 120',
      a: '① 2² × 3²  ② 2³ × 3 × 5',
      hint: '2, 3, 5, 7... 순서로 나누어 떨어지는지 확인하세요.',
      solutionSteps: [
        { label: '① 36 소인수분해', content: '36 ÷ 2 = 18 → 18 ÷ 2 = 9 → 9 ÷ 3 = 3 → 3 ÷ 3 = 1\n36 = 2 × 2 × 3 × 3 = 2² × 3²' },
        { label: '② 120 소인수분해', content: '120 ÷ 2 = 60 → 60 ÷ 2 = 30 → 30 ÷ 2 = 15 → 15 ÷ 3 = 5 → 5 ÷ 5 = 1\n120 = 2 × 2 × 2 × 3 × 5 = 2³ × 3 × 5' },
      ]
    },
    misconceptions: [
      { code: 'NM05', desc: '소인수분해를 할 때 4 × 9 = 36처럼 합성수 곱으로 끝낸다 → 반드시 소수의 곱으로!' },
      { code: 'NM06', desc: '12 = 2² × 3이라는 결과가 방법에 따라 달라진다고 생각한다 → 결과는 항상 유일!' },
    ],
  },
  {
    id: 'NS04',
    order: 4,
    title: '최대공약수(GCD)',
    subtitle: '두 수의 공통 약수 중 가장 큰 수를 구한다',
    difficulty: 'intermediate' as const,
    teaches: ['GCD', 'CommonFactor', 'PrimeFactorMethod'],
    icon: '🤝',
    color: { bg: 'bg-teal-50', border: 'border-teal-200', badge: 'bg-teal-100 text-teal-700', accent: 'text-teal-600', btn: 'bg-teal-500' },
    objective: '소인수분해를 이용하여 두 수 이상의 최대공약수(GCD)를 구할 수 있다.',
    concept: `최대공약수(GCD, Greatest Common Divisor)는 두 수 이상의 공약수 중 가장 큰 수입니다.

📌 소인수분해로 GCD 구하기
① 각 수를 소인수분해 한다
② 공통 소인수를 찾는다
③ 공통 소인수의 지수 중 작은 것을 선택한다
④ 곱한다

예) GCD(12, 18) 구하기
  12 = 2² × 3
  18 = 2  × 3²
  공통: 2^min(2,1) × 3^min(1,2) = 2¹ × 3¹ = 6
  → GCD = 6

📌 나눗셈 사다리로 GCD 구하기 (빠른 방법)
  2 | 12  18
  3 |  6   9
     2    3
→ GCD = 2 × 3 = 6

⚠️ 수포자 탈출 포인트
최대공약수 = "두 수가 같이 나눠지는 가장 큰 수"
→ 직사각형을 정사각형으로 채울 때 가장 큰 한 변의 길이!`,
    examples: [
      { q: 'GCD(24, 36)을 소인수분해를 이용하여 구하세요.', a: '12', detail: '24 = 2³ × 3\n36 = 2² × 3²\n공통: 2^min(3,2) × 3^min(1,2) = 2² × 3 = 4 × 3 = 12' },
      { q: 'GCD(60, 90, 120)을 구하세요.', a: '30', detail: '60 = 2² × 3 × 5\n90 = 2 × 3² × 5\n120 = 2³ × 3 × 5\n공통: 2^min(2,1,3) × 3^min(1,2,1) × 5^min(1,1,1) = 2¹ × 3¹ × 5¹ = 30' },
    ],
    keyPoints: ['공통 소인수만 선택', '지수는 작은 것을 선택', 'GCD는 항상 원래 수보다 작거나 같음'],
    practice: {
      q: '소인수분해를 이용하여 GCD(48, 72)를 구하세요.',
      a: '24',
      hint: '48과 72를 각각 소인수분해하고, 공통 소인수의 낮은 지수를 선택하세요.',
      solutionSteps: [
        { label: '① 48 소인수분해', content: '48 = 2⁴ × 3' },
        { label: '② 72 소인수분해', content: '72 = 2³ × 3²' },
        { label: '③ 공통 소인수 선택', content: '2: min(4,3)=3, 3: min(1,2)=1\n→ 2³ × 3¹ = 8 × 3 = 24' },
        { label: '④ 확인', content: '48 ÷ 24 = 2, 72 ÷ 24 = 3 ✓' },
      ]
    },
    misconceptions: [
      { code: 'NM07', desc: 'GCD를 구할 때 지수가 큰 것을 선택한다 → GCD는 공통 부분이므로 지수가 작은 것을 선택!' },
    ],
  },
  {
    id: 'NS05',
    order: 5,
    title: '최소공배수(LCM)',
    subtitle: '두 수의 공통 배수 중 가장 작은 수를 구한다',
    difficulty: 'intermediate' as const,
    teaches: ['LCM', 'CommonMultiple', 'PrimeFactorMethod'],
    icon: '🔄',
    color: { bg: 'bg-purple-50', border: 'border-purple-200', badge: 'bg-purple-100 text-purple-700', accent: 'text-purple-600', btn: 'bg-purple-500' },
    objective: '소인수분해를 이용하여 두 수 이상의 최소공배수(LCM)를 구할 수 있다.',
    concept: `최소공배수(LCM, Least Common Multiple)는 두 수 이상의 공배수 중 가장 작은 수입니다.

📌 소인수분해로 LCM 구하기
① 각 수를 소인수분해 한다
② 모든 소인수를 찾는다 (한쪽에만 있어도 포함)
③ 각 소인수의 지수 중 큰 것을 선택한다
④ 곱한다

예) LCM(12, 18) 구하기
  12 = 2² × 3
  18 = 2  × 3²
  모든 소인수: 2^max(2,1) × 3^max(1,2) = 2² × 3² = 4 × 9 = 36
  → LCM = 36

📌 나눗셈 사다리로 LCM 구하기
  2 | 12  18
  3 |  6   9
     2    3
→ LCM = 2 × 3 × 2 × 3 = 36

📌 GCD와 LCM의 관계
  LCM(a,b) × GCD(a,b) = a × b
  → LCM = (a × b) ÷ GCD`,
    examples: [
      { q: 'LCM(24, 36)을 소인수분해를 이용하여 구하세요.', a: '72', detail: '24 = 2³ × 3\n36 = 2² × 3²\n모든 소인수: 2^max(3,2) × 3^max(1,2) = 2³ × 3² = 8 × 9 = 72' },
      { q: 'LCM(4, 6, 9)를 구하세요.', a: '36', detail: '4 = 2²\n6 = 2 × 3\n9 = 3²\n모든 소인수: 2^max(2,1,0) × 3^max(0,1,2) = 2² × 3² = 4 × 9 = 36' },
    ],
    keyPoints: ['모든 소인수를 포함', '지수는 큰 것을 선택', 'GCD × LCM = a × b'],
    practice: {
      q: '소인수분해를 이용하여 LCM(18, 30)을 구하세요.',
      a: '90',
      hint: '18과 30을 소인수분해하고, 모든 소인수의 높은 지수를 선택하세요.',
      solutionSteps: [
        { label: '① 18 소인수분해', content: '18 = 2 × 3²' },
        { label: '② 30 소인수분해', content: '30 = 2 × 3 × 5' },
        { label: '③ 모든 소인수 선택', content: '2: max(1,1)=1, 3: max(2,1)=2, 5: max(0,1)=1\n→ 2¹ × 3² × 5¹ = 2 × 9 × 5 = 90' },
        { label: '④ 확인 (GCD 이용)', content: 'GCD(18,30)=6, 18×30=540, 540÷6=90 ✓' },
      ]
    },
    misconceptions: [
      { code: 'NM08', desc: 'LCM을 구할 때 지수가 작은 것을 선택한다 → LCM은 모두 포함해야 하므로 지수가 큰 것을 선택!' },
      { code: 'NM09', desc: 'LCM이 항상 두 수의 곱이라고 생각한다 → 공약수가 있으면 LCM < 두 수의 곱' },
    ],
  },
  {
    id: 'NS06',
    order: 6,
    title: 'GCD·LCM 활용 문제',
    subtitle: '최대공약수와 최소공배수를 실생활에 적용한다',
    difficulty: 'advanced' as const,
    teaches: ['GCDApplication', 'LCMApplication', 'WordProblem'],
    icon: '🧩',
    color: { bg: 'bg-orange-50', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-700', accent: 'text-orange-600', btn: 'bg-orange-500' },
    objective: 'GCD, LCM을 활용한 실생활 문제를 풀고, 어떤 상황에 GCD/LCM이 필요한지 판단할 수 있다.',
    concept: `GCD와 LCM은 다양한 실생활 문제에 쓰입니다.

📌 GCD를 쓰는 상황: "나누기, 똑같이 나누기"
• 가장 크게 자르기 (타일, 케이크 등)
• 가능한 한 많은 그룹으로 나누기
• "최대한 크게" = GCD

예) 가로 24cm, 세로 36cm 직사각형을 같은 크기의 정사각형 타일로 채우려고 한다.
    타일의 최대 크기는?
    → GCD(24, 36) = 12 → 12cm × 12cm 타일

📌 LCM을 쓰는 상황: "함께 만나기, 다시 시작하기"
• 두 사람이 다시 같은 지점에 만나는 시간
• 신호등이 다시 같이 바뀌는 시간
• "언제 다시 같아지나" = LCM

예) A버스는 6분, B버스는 9분마다 출발한다.
    두 버스가 동시에 출발한 후 다시 동시에 출발하는 시간은?
    → LCM(6, 9) = 18 → 18분 후

⚠️ 핵심 판단 기준
"나누는 문제" → GCD
"만나는/겹치는 문제" → LCM`,
    examples: [
      { q: '사탕 24개와 초콜릿 36개를 최대한 많은 학생에게 동일하게 나눠줄 때, 학생 수와 각자 받는 양은?', a: 'GCD(24,36)=12 → 12명, 사탕 2개·초콜릿 3개씩', detail: 'GCD(24, 36) = 12 → 12명에게 나눌 수 있음\n각 학생: 사탕 24÷12=2개, 초콜릿 36÷12=3개' },
      { q: '철수는 4일마다, 영희는 6일마다 수영장에 간다. 오늘 같이 갔다면 다음에 같이 가는 날은?', a: 'LCM(4,6)=12 → 12일 후', detail: 'LCM(4, 6) = 2² × 3 = 12\n12일 후에 다시 같이 수영장을 가게 됩니다.' },
    ],
    keyPoints: ['나누기·자르기 → GCD', '만나기·겹치기 → LCM', '문제 상황을 GCD/LCM으로 판단'],
    practice: {
      q: '가로 48cm, 세로 72cm인 직사각형 벽에 같은 크기의 정사각형 타일을 빈틈없이 붙이려 한다. ① 타일 한 변의 최대 길이는? ② 이 타일은 몇 장 필요한가?',
      a: '① 24cm  ② 6장',
      hint: '① GCD(48, 72) = 타일 최대 한 변, ② 벽 넓이 ÷ 타일 넓이',
      solutionSteps: [
        { label: '① GCD(48, 72) 계산', content: '48 = 2⁴ × 3, 72 = 2³ × 3²\nGCD = 2³ × 3 = 24 → 한 변 24cm' },
        { label: '② 타일 수 계산', content: '벽 넓이 = 48 × 72 = 3456 cm²\n타일 넓이 = 24 × 24 = 576 cm²\n타일 수 = 3456 ÷ 576 = 6장' },
        { label: '③ 검산', content: '가로: 48 ÷ 24 = 2장, 세로: 72 ÷ 24 = 3장\n총 2 × 3 = 6장 ✓' },
      ]
    },
    misconceptions: [
      { code: 'NM10', desc: '"나눠주는" 문제에 LCM을 쓴다 → 나누는 것은 GCD, 만나는 것은 LCM!' },
    ],
  },
  {
    id: 'NS07',
    order: 7,
    title: '오답 분석과 개념 정리',
    subtitle: '소인수분해 단원 전체 오개념을 점검한다',
    difficulty: 'advanced' as const,
    teaches: ['MisconceptionReview', 'ConceptSummary'],
    icon: '🔍',
    color: { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-700', accent: 'text-red-600', btn: 'bg-red-500' },
    objective: '소인수분해 단원에서 자주 발생하는 오개념을 파악하고, 스스로 점검할 수 있다.',
    concept: `소인수분해 단원 전체 오개념 정리

❌ NM01: 1을 소수로 착각
  소수 정의: 약수가 정확히 2개 → 1은 약수 1개뿐 → 소수 아님!

❌ NM02: 2가 합성수라고 착각
  2의 약수: {1, 2} → 딱 2개 → 소수! (유일한 짝수 소수)

❌ NM03: 2³ = 2×3 = 6으로 계산
  2³ = 2×2×2 = 8 (지수는 곱하는 횟수)

❌ NM04: 소인수분해를 합성수로 끝냄
  36 = 4 × 9 (×) → 36 = 2² × 3² (○)

❌ NM05: GCD에서 지수 큰 것 선택
  GCD는 공통 부분 → 지수 작은 것 선택!
  LCM은 모두 포함 → 지수 큰 것 선택!

✅ 단원 핵심 공식 정리
• 소수: 약수가 딱 2개인 수 (1과 자기 자신)
• aⁿ: a를 n번 곱한 것
• GCD(소인수분해): 공통 소인수 × 낮은 지수
• LCM(소인수분해): 모든 소인수 × 높은 지수
• GCD × LCM = a × b`,
    examples: [
      { q: '다음 중 틀린 것을 찾고 올바르게 고치세요: "GCD(12, 18) = 2² × 3² = 36"', a: '틀림! GCD = 2¹ × 3¹ = 6 (지수는 작은 것을 선택)', detail: '12 = 2² × 3, 18 = 2 × 3²\nGCD: 2^min(2,1) × 3^min(1,2) = 2¹ × 3¹ = 6\n(지수가 큰 것을 선택하면 LCM이 됩니다!)' },
      { q: '소인수분해와 GCD, LCM의 관계를 12와 18로 확인하세요.', a: 'GCD=6, LCM=36, GCD×LCM = 6×36 = 216 = 12×18 ✓', detail: '12×18 = 216\nGCD(12,18) = 6, LCM(12,18) = 36\n6 × 36 = 216 = 12 × 18 ← 공식 확인!' },
    ],
    keyPoints: ['GCD: 낮은 지수 선택', 'LCM: 높은 지수 선택', 'GCD × LCM = a × b'],
    practice: {
      q: '다음 풀이에서 틀린 부분을 찾고 올바른 풀이를 쓰세요.\n"LCM(8, 12) 구하기: 8=2³, 12=2²×3 → LCM = 2² × 3 = 12"',
      a: '틀림! LCM = 2³ × 3 = 24 (지수는 큰 것을 선택해야 함)',
      hint: 'LCM은 "모두 포함"이므로 지수가 큰 것을 선택해야 합니다.',
      solutionSteps: [
        { label: '① 오류 확인', content: '8=2³, 12=2²×3에서 2의 지수: min(3,2)=2는 GCD를 구하는 방법!' },
        { label: '② 올바른 LCM', content: 'LCM: 2의 지수 max(3,2)=3, 3의 지수 max(0,1)=1\n→ 2³ × 3¹ = 8 × 3 = 24' },
        { label: '③ 검산', content: 'GCD(8,12)=4, 8×12=96, 96÷4=24=LCM ✓' },
      ]
    },
    misconceptions: [
      { code: 'NM11', desc: 'GCD와 LCM에서 지수 선택 기준을 반대로 적용한다 → GCD=작은 지수, LCM=큰 지수!' },
    ],
  },
  {
    id: 'NS08',
    order: 8,
    title: '수포자를 위한 처음부터',
    subtitle: '소인수분해가 왜 어렵게 느껴지는지 이해한다',
    difficulty: 'beginner' as const,
    teaches: ['ConceptFromScratch', 'AnxietyReduction', 'StepByStep'],
    icon: '🌱',
    color: { bg: 'bg-yellow-50', border: 'border-yellow-200', badge: 'bg-yellow-100 text-yellow-700', accent: 'text-yellow-600', btn: 'bg-yellow-500' },
    objective: '소인수분해의 핵심을 이야기로 이해하고, "나눗셈"만 할 수 있으면 소인수분해도 할 수 있다는 자신감을 갖는다.',
    concept: `"소인수분해"라는 말이 어렵게 느껴지나요? 실제로는 나눗셈만 알면 됩니다!

🌱 수포자 탈출 이야기

소인수분해를 처음 보면 이렇게 생각할 수 있어요:
"소수? 합성수? 소인수? 무슨 말인지 하나도 모르겠어..."

하지만 이렇게 생각해 보세요:

🍕 피자 12조각을 똑같이 나누기
→ 12를 나눌 수 있는 수는? 2, 3, 4, 6, 12 → 이게 약수!
→ 그 중 소수는? 2, 3 → 이게 소인수!
→ 12 = 2² × 3 → 이게 소인수분해!

📦 박스 안에 작은 박스 넣기
→ 큰 수 = 큰 박스
→ 소인수분해 = 큰 박스를 더 이상 나눌 수 없는 소수 박스로 쪼개기

🎯 핵심 3단계 (이것만 기억!)
① 2로 나누어 지면 2로 나눈다
② 2로 안 나누어지면 3으로 나눈다
③ 몫이 1이 될 때까지 반복!

✨ 예시: 12
2 | 12
2 |  6
3 |  3
    1
12 = 2 × 2 × 3 = 2² × 3

💪 "나눗셈"만 할 수 있으면 소인수분해도 할 수 있습니다!`,
    examples: [
      { q: '18을 수포자 방법(작은 소수부터 나누기)으로 소인수분해해 보세요.', a: '18 = 2 × 3²', detail: '2 | 18  ← 18 ÷ 2 = 9 (나누어 떨어짐)\n3 |  9  ← 9 ÷ 3 = 3 (나누어 떨어짐)\n3 |  3  ← 3 ÷ 3 = 1\n    1\n18 = 2 × 3 × 3 = 2 × 3²\n쉽죠? 그냥 나눗셈이에요!' },
      { q: '"나는 수학이 너무 싫었는데 소인수분해부터 다시 시작하고 싶다" → 어디서 시작하면 될까요?', a: '나눗셈부터! 12 ÷ 2 = 6, 6 ÷ 2 = 3, 3 ÷ 3 = 1 → 12 = 2² × 3', detail: '소인수분해의 90%는 나눗셈입니다.\n나눗셈만 할 줄 알면 소인수분해를 할 수 있어요.\n천천히, 작은 소수부터 시작하면 됩니다!' },
    ],
    keyPoints: ['나눗셈만 할 수 있으면 OK', '2 → 3 → 5 → 7 순서로 나누기', '천천히 해도 맞으면 된다'],
    practice: {
      q: '아래 단계를 따라 24를 소인수분해해 보세요.\n□ | 24  ← 24 ÷ □ = ?\n□ | □   ← ? ÷ □ = ?\n□ | □   ← ? ÷ □ = ?\n    1\n24 = □ × □ × □ = □',
      a: '2|24 → 2|12 → 2|6 → 3|3 → 1\n24 = 2³ × 3',
      hint: '첫 번째 칸에는 항상 2를 먼저 써보세요! 24 ÷ 2 = 12로 시작!',
      solutionSteps: [
        { label: '① 2로 나누기', content: '24 ÷ 2 = 12  (2는 24의 약수!)' },
        { label: '② 또 2로 나누기', content: '12 ÷ 2 = 6  (6도 짝수니까 또 2로!)' },
        { label: '③ 또 2로 나누기', content: '6 ÷ 2 = 3  (3은 홀수이고 소수!)' },
        { label: '④ 3으로 나누기', content: '3 ÷ 3 = 1  (끝!)' },
        { label: '⑤ 결과', content: '24 = 2 × 2 × 2 × 3 = 2³ × 3  🎉' },
      ]
    },
    misconceptions: [
      { code: 'NM12', desc: '소인수분해가 어렵다는 선입견 → 나눗셈만 하면 돼요! 천천히 하면 반드시 됩니다.' },
    ],
  },
]

// ── 난이도 설정 ────────────────────────────────────────────────────────────────
const DIFFICULTY_CONFIG = {
  beginner:     { label: '기초', color: 'bg-green-100 text-green-700' },
  intermediate: { label: '보통', color: 'bg-yellow-100 text-yellow-700' },
  advanced:     { label: '심화', color: 'bg-red-100 text-red-700' },
}

// ── PracticeBox ────────────────────────────────────────────────────────────────
function PracticeBox({
  practice, stepId, unitId, alreadyAnswered, onAnswered
}: {
  practice: typeof STEPS[0]['practice']
  stepId: string
  unitId: string
  alreadyAnswered: boolean
  onAnswered: () => void
}) {
  const [show, setShow] = useState(false)
  const [revealed, setRevealed] = useState(alreadyAnswered)
  const [stepOpen, setStepOpen] = useState(false)

  return (
    <div className="card border-2 border-primary-100">
      <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
        <PenTool size={16} className="text-primary-500" />
        연습 문제
      </h3>
      <div className="bg-primary-50 rounded-xl p-4 mb-3">
        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{practice.q}</p>
      </div>
      {!revealed ? (
        <div className="space-y-2">
          <button
            onClick={() => setShow(!show)}
            className="text-xs text-primary-500 hover:text-primary-700 flex items-center gap-1"
          >
            💡 힌트 {show ? '숨기기' : '보기'}
          </button>
          {show && (
            <div className="text-xs bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 text-yellow-800">
              {practice.hint}
            </div>
          )}
          <button
            onClick={() => { setRevealed(true); onAnswered() }}
            className="w-full py-2 rounded-xl bg-primary-500 text-white text-sm font-bold hover:bg-primary-600 transition-colors"
          >
            정답 확인
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
            <p className="text-xs font-bold text-emerald-600 mb-1">✅ 정답</p>
            <p className="text-sm font-bold text-slate-800 whitespace-pre-line">{practice.a}</p>
          </div>
          <button
            onClick={() => setStepOpen(!stepOpen)}
            className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
          >
            <ArrowRight size={10} className={`transition-transform ${stepOpen ? 'rotate-90' : ''}`} />
            {stepOpen ? '풀이 닫기' : '단계별 풀이 보기'}
          </button>
          {stepOpen && (
            <div className="space-y-2">
              {practice.solutionSteps.map((s, i) => (
                <div key={i} className="bg-slate-50 rounded-lg px-3 py-2">
                  <p className="text-xs font-bold text-slate-600 mb-0.5">{s.label}</p>
                  <p className="text-xs text-slate-700 whitespace-pre-line">{s.content}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── ExampleCard ────────────────────────────────────────────────────────────────
function ExampleCard({ ex, idx }: { ex: typeof STEPS[0]['examples'][0]; idx: number }) {
  const [open, setOpen] = useState(false)

  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
      <div className="p-3">
        <p className="text-xs font-bold text-slate-400 mb-1">예시 {idx + 1}</p>
        <p className="text-sm text-slate-800 whitespace-pre-line">{ex.q}</p>
        <div className="mt-2 bg-primary-50 border border-primary-200 rounded-lg px-3 py-2">
          <p className="text-sm font-bold text-primary-700">💡 {ex.a}</p>
        </div>
      </div>
      {ex.detail && (
        <button
          onClick={() => setOpen(!open)}
          className="w-full text-left px-3 py-2 bg-slate-50 border-t border-slate-100 text-xs text-slate-500 hover:bg-slate-100 transition-colors flex items-center gap-1"
        >
          <ArrowRight size={10} className={`transition-transform ${open ? 'rotate-90' : ''}`} />
          {open ? '풀이 숨기기' : '상세 풀이 보기'}
        </button>
      )}
      {open && ex.detail && (
        <div className="px-3 pb-3 bg-slate-50 text-xs text-slate-600 leading-relaxed whitespace-pre-line">
          {ex.detail}
        </div>
      )}
    </div>
  )
}

// ── 스토리카드 컴포넌트 ────────────────────────────────────────────────────────
function StoryCard({ stepId, color }: { stepId: string; color: typeof STEPS[0]['color'] }) {
  const story = STEP_STORIES[stepId]
  const [lineIdx, setLineIdx] = useState(0)
  const [finished, setFinished] = useState(false)

  if (!story) return null

  const currentLine = story.lines[lineIdx]
  const isLast = lineIdx === story.lines.length - 1

  const speakerColors: Record<string, string> = {
    teacher:  'from-indigo-500 to-blue-600',
    student:  'from-emerald-500 to-teal-600',
    friend:   'from-purple-500 to-pink-500',
    narrator: 'from-amber-400 to-orange-500',
  }
  const bgColors: Record<string, string> = {
    teacher:  'bg-indigo-50 border-indigo-200',
    student:  'bg-emerald-50 border-emerald-200',
    friend:   'bg-purple-50 border-purple-200',
    narrator: 'bg-amber-50 border-amber-200',
  }

  return (
    <div className="rounded-2xl border-2 border-slate-200 bg-white overflow-hidden shadow-sm">
      {/* 헤더 */}
      <div className={`bg-gradient-to-r ${color.border.replace('border-', 'from-').replace('-200', '-100')} to-white px-4 py-2.5 border-b border-slate-100 flex items-center gap-2`}>
        <MessageCircle size={13} className={color.accent} />
        <span className={`text-xs font-black ${color.accent}`}>📖 이야기로 시작하기</span>
        <span className="text-xs text-slate-400 ml-auto">{lineIdx + 1}/{story.lines.length}</span>
      </div>

      {/* 장면 */}
      <div className="px-4 pt-3 pb-1">
        <p className="text-xs text-slate-500 italic leading-relaxed">{story.scene}</p>
      </div>

      {/* 진행 바 */}
      <div className="mx-4 mb-3 bg-slate-100 rounded-full h-1 overflow-hidden">
        <div
          className={`h-full bg-gradient-to-r ${speakerColors[currentLine.speaker]} rounded-full transition-all duration-500`}
          style={{ width: `${((lineIdx + 1) / story.lines.length) * 100}%` }}
        />
      </div>

      {/* 대화 박스 */}
      <div className="px-4 pb-3">
        <div className={`rounded-xl border ${bgColors[currentLine.speaker]} p-3 min-h-[80px] transition-all duration-300`}>
          {currentLine.speaker !== 'narrator' ? (
            <div className="flex items-start gap-2.5">
              <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${speakerColors[currentLine.speaker]} flex items-center justify-center text-lg flex-shrink-0 shadow-sm`}>
                {currentLine.emoji}
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold text-slate-500 mb-0.5">{currentLine.name}</p>
                <p className="text-sm text-slate-800 leading-relaxed">{currentLine.text}</p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2">
              <span className="text-lg">{currentLine.emoji}</span>
              <p className="text-sm text-amber-800 leading-relaxed italic flex-1">{currentLine.text}</p>
            </div>
          )}
        </div>

        {/* 수학 연결 (마지막 줄 완료 후) */}
        {finished && (
          <div className={`mt-3 rounded-xl border-2 ${color.border} ${color.bg} px-4 py-3 flex items-center gap-2`}>
            <span className="text-xl">{story.revealEmoji}</span>
            <div>
              <p className="text-xs font-black text-slate-600 mb-0.5">✨ 수학 연결</p>
              <p className={`text-xs font-bold ${color.accent} leading-relaxed`}>{story.reveal}</p>
            </div>
          </div>
        )}

        {/* 버튼 */}
        <div className="flex gap-2 mt-3">
          {lineIdx > 0 && (
            <button
              onClick={() => { setLineIdx(i => i - 1); setFinished(false) }}
              className="text-xs px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 font-medium transition-colors"
            >
              ← 이전
            </button>
          )}
          {!isLast ? (
            <button
              onClick={() => setLineIdx(i => i + 1)}
              className={`flex-1 py-1.5 rounded-xl text-white text-xs font-bold ${color.btn} hover:opacity-90 transition-opacity flex items-center justify-center gap-1`}
            >
              <Play size={11} /> 다음 대화
            </button>
          ) : !finished ? (
            <button
              onClick={() => setFinished(true)}
              className={`flex-1 py-1.5 rounded-xl text-white text-xs font-bold bg-gradient-to-r from-amber-400 to-orange-500 hover:opacity-90 transition-opacity flex items-center justify-center gap-1`}
            >
              <Sparkles size={11} /> 수학 연결 보기
            </button>
          ) : (
            <button
              onClick={() => { setLineIdx(0); setFinished(false) }}
              className="flex-1 py-1.5 rounded-xl text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
            >
              🔄 처음부터
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ── 예상 파편 패널 컴포넌트 ───────────────────────────────────────────────────
function PredictedFragmentPanel({
  fragments,
  onGoStep,
}: {
  fragments: PredictedFragment[]
  onGoStep: (stepId: string) => void
}) {
  const [open, setOpen] = useState(true)
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())

  const visible = fragments.filter(f => !dismissed.has(f.fragment.id))
  if (visible.length === 0) return null

  const dangerCount  = visible.filter(f => f.tag === 'danger').length
  const warningCount = visible.filter(f => f.tag === 'warning').length

  return (
    <div className="rounded-2xl border-2 border-violet-200 bg-violet-50 overflow-hidden">
      {/* 헤더 */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-violet-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <TrendingUp size={15} className="text-violet-600" />
          <span className="text-sm font-black text-violet-800">🤖 AI 예상 파편 분석</span>
          {dangerCount > 0 && (
            <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-bold flex items-center gap-1">
              <Flame size={10} /> 긴급 {dangerCount}
            </span>
          )}
          {warningCount > 0 && (
            <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-bold">
              주의 {warningCount}
            </span>
          )}
        </div>
        <ChevronDown size={14} className={`text-violet-600 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-2">
          <p className="text-xs text-violet-600 mb-3">
            학습 기록을 분석해 <strong>틀릴 가능성이 높은 개념</strong>을 자동으로 추려냈습니다. 먼저 확인해 보세요!
          </p>
          {visible.map(pred => {
            const tagConf = FRAGMENT_TAG_CONFIG[pred.tag]
            return (
              <div
                key={pred.fragment.id}
                className={`rounded-xl border ${tagConf.color} p-3 relative`}
              >
                {/* 닫기 */}
                <button
                  onClick={() => setDismissed(d => new Set([...d, pred.fragment.id]))}
                  className="absolute top-2 right-2 text-slate-400 hover:text-slate-600"
                >
                  <X size={12} />
                </button>

                <div className="flex items-start gap-2 pr-5">
                  <span className="text-xl">{pred.fragment.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${tagConf.badge}`}>
                        {tagConf.label}
                      </span>
                      <span className="text-xs font-mono text-slate-500">{pred.fragment.misconceptionCode}</span>
                    </div>
                    <p className="text-xs font-bold text-slate-800 leading-tight">{pred.fragment.title}</p>
                    <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">{pred.fragment.description}</p>
                    {/* 이유 */}
                    <div className="mt-1.5 space-y-0.5">
                      {pred.reason.map((r, i) => (
                        <p key={i} className="text-xs text-slate-500">{r}</p>
                      ))}
                    </div>
                    {/* 수정 팁 */}
                    <div className="mt-2 bg-white/60 rounded-lg px-2 py-1.5">
                      <p className="text-xs font-semibold text-slate-700">
                        💡 <span className="font-bold">고치는 법:</span> {pred.fragment.fixTip}
                      </p>
                    </div>
                    {/* 단계 이동 버튼 */}
                    <button
                      onClick={() => onGoStep(pred.fragment.stepId)}
                      className="mt-2 text-xs px-3 py-1 rounded-lg bg-violet-600 text-white font-bold hover:bg-violet-700 transition-colors"
                    >
                      → {pred.fragment.stepTitle} 단계로 바로 가기
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────
const UNIT_ID = 'N1-1'

export default function N1LearnPage({ defaultStepId }: { defaultStepId?: string | null }) {
  const [selectedId, setSelectedId] = useState<string | null>(defaultStepId ?? null)
  const [videoModal, setVideoModal] = useState<{ stepId: string; label: string } | null>(null)
  const getApprovedByTag   = useVideoStore(s => s.getApprovedByTag)
  const getRecruitingByTag = useVideoStore(s => s.getRecruitingByTag)
  const { completedCount, steps: stepProgress, pct } = useLearnProgress(UNIT_ID)
  const { completeStep, init } = useProgressStore(s => ({ completeStep: s.completeStep, init: s.init }))
  const unitProgress = useUnitProgress(UNIT_ID)
  const predictedFragments = computePredictedFragments(unitProgress, 4)

  useEffect(() => { init('student_001') }, [])

  useEffect(() => {
    if (defaultStepId) {
      setSelectedId(defaultStepId)
      setTimeout(() => {
        const el = document.getElementById(`step-${defaultStepId}`)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 300)
    }
  }, [defaultStepId])

  const selected = STEPS.find(s => s.id === selectedId) ?? null
  const selectedIdx = STEPS.findIndex(s => s.id === selectedId)

  const isStepCompleted = (id: string) =>
    stepProgress[id]?.status === 'completed' || stepProgress[id]?.status === 'mastered'
  const isStepPracticed = (id: string) => stepProgress[id]?.practiceAnswered ?? false

  const handleComplete = (stepId: string) => {
    const step = STEPS.find(s => s.id === stepId)
    completeStep(UNIT_ID, stepId, step?.title)
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
          <span>중1</span>
          <ChevronRight size={12} />
          <span>수와 연산</span>
          <ChevronRight size={12} />
          <span className="font-semibold text-slate-700">소인수분해</span>
        </div>
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <Hash size={24} className="text-blue-500" />
          소인수분해 (N1-1)
        </h1>
        <p className="text-sm text-slate-500 mt-1">소수·합성수부터 GCD·LCM 활용까지 — 중1 수와 연산의 첫걸음</p>

        {/* 진도 바 */}
        <div className="mt-4 bg-slate-100 rounded-full h-2.5 overflow-hidden">
          <div
            className="h-full bg-blue-500 rounded-full transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="mt-1.5 flex items-center justify-between">
          <p className="text-xs text-slate-500">
            {completedCount}/{STEPS.length} 단계 완료
          </p>
          <p className="text-xs font-bold text-blue-600">{pct}%</p>
        </div>
      </div>

      {/* 수포자 탈출 배너 */}
      <div className="mb-5 rounded-2xl bg-gradient-to-r from-yellow-400 to-orange-400 p-4 text-white flex items-center gap-3">
        <span className="text-3xl">🌱</span>
        <div>
          <p className="font-black text-sm">수포자 탈출 코너 — NS08 단계를 먼저 보세요!</p>
          <p className="text-xs opacity-90 mt-0.5">소인수분해가 처음이거나 다시 시작하고 싶다면, NS08 "처음부터" 단계부터 시작하세요.</p>
        </div>
        <button
          onClick={() => setSelectedId('NS08')}
          className="ml-auto flex-shrink-0 bg-white/20 hover:bg-white/30 text-white text-xs font-bold px-3 py-1.5 rounded-xl transition-colors"
        >
          시작 →
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Step List + 예상 파편 패널 */}
        <div className="lg:col-span-1 space-y-2">
          <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">학습 단계</h2>
          {STEPS.map(step => {
            const completed = isStepCompleted(step.id)
            const practiced = isStepPracticed(step.id)
            const isActive = step.id === selectedId
            const diffConf = DIFFICULTY_CONFIG[step.difficulty]

            return (
              <button
                key={step.id}
                id={`step-${step.id}`}
                onClick={() => setSelectedId(isActive ? null : step.id)}
                className={`w-full text-left rounded-xl border-2 p-3 transition-all ${
                  isActive
                    ? `${step.color.border} ${step.color.bg} shadow-sm`
                    : 'border-slate-100 bg-white hover:border-slate-200 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-lg flex-shrink-0 ${
                    completed ? 'bg-emerald-100' : step.color.bg
                  }`}>
                    {completed ? '✅' : step.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className={`text-xs font-bold ${step.color.accent}`}>{step.id}</span>
                      {step.id === 'NS08' && (
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded-full font-bold">수포자</span>
                      )}
                    </div>
                    <p className={`text-sm font-semibold truncate ${isActive ? 'text-slate-800' : 'text-slate-700'}`}>
                      {step.title}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${diffConf.color}`}>
                        {diffConf.label}
                      </span>
                      {practiced && !completed && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-blue-100 text-blue-600">연습완료</span>
                      )}
                    </div>
                  </div>
                  <ChevronRight
                    size={14}
                    className={`flex-shrink-0 transition-transform ${isActive ? 'rotate-90 ' + step.color.accent : 'text-slate-300'}`}
                  />
                </div>
              </button>
            )
          })}

          {/* ── 예상 파편 패널 (스텝 목록 아래) ── */}
          {predictedFragments.length > 0 && (
            <div className="mt-4">
              <PredictedFragmentPanel
                fragments={predictedFragments}
                onGoStep={(stepId) => setSelectedId(stepId)}
              />
            </div>
          )}
        </div>

        {/* Step Detail */}
        <div className="lg:col-span-2">
          {!selected ? (
            <div className="card flex flex-col items-center justify-center py-16 text-center">
              <div className="text-6xl mb-4">🔢</div>
              <p className="text-lg font-bold text-slate-600 mb-1">학습 단계를 선택하세요</p>
              <p className="text-sm text-slate-400">왼쪽 목록에서 원하는 단계를 클릭하면<br />개념 설명과 예시, 연습 문제가 나타납니다.</p>
              {completedCount > 0 && (
                <div className="mt-4 px-4 py-2.5 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <p className="text-sm font-semibold text-emerald-700">
                    🎉 {completedCount}단계 완료! 계속 학습하세요.
                  </p>
                </div>
              )}
              {/* 오늘의 추천 단계 */}
              {predictedFragments.length > 0 && (
                <div className="mt-5 w-full max-w-sm">
                  <p className="text-xs font-bold text-violet-600 mb-2 flex items-center justify-center gap-1">
                    <TrendingUp size={12} /> AI 추천 오늘 먼저 볼 단계
                  </p>
                  <div className="space-y-1.5">
                    {predictedFragments.slice(0, 3).map(pred => (
                      <button
                        key={pred.fragment.id}
                        onClick={() => setSelectedId(pred.fragment.stepId)}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-xl bg-violet-50 border border-violet-200 hover:bg-violet-100 transition-colors text-left"
                      >
                        <span>{pred.fragment.emoji}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-slate-700 truncate">{pred.fragment.stepTitle}</p>
                          <p className="text-xs text-violet-600 truncate">{pred.fragment.title}</p>
                        </div>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold flex-shrink-0 ${FRAGMENT_TAG_CONFIG[pred.tag].badge}`}>
                          {pred.tag === 'danger' ? '긴급' : pred.tag === 'warning' ? '주의' : '추천'}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-4 flex flex-wrap gap-2 justify-center">
                {STEPS.slice(0, 4).map(s => (
                  <button
                    key={s.id}
                    onClick={() => setSelectedId(s.id)}
                    className="text-xs px-3 py-1.5 rounded-full border border-slate-200 hover:bg-slate-50 transition-colors text-slate-600"
                  >
                    {s.icon} {s.title}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Header Card */}
              <div className={`rounded-2xl border-2 ${selected.color.border} ${selected.color.bg} p-5`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="text-4xl">{selected.icon}</span>
                    <div>
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${selected.color.badge}`}>{selected.id}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${DIFFICULTY_CONFIG[selected.difficulty].color}`}>
                          {DIFFICULTY_CONFIG[selected.difficulty].label}
                        </span>
                        {selected.id === 'NS08' && (
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-bold">🌱 수포자 탈출</span>
                        )}
                        {isStepCompleted(selected.id) && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold flex items-center gap-1">
                            <CheckCircle size={10} /> 완료
                          </span>
                        )}
                      </div>
                      <h2 className="text-xl font-black text-slate-800">{selected.title}</h2>
                      <p className="text-sm text-slate-500 mt-0.5">{selected.subtitle}</p>
                    </div>
                  </div>
                </div>
                <div className="mt-3 p-3 bg-white/70 rounded-xl">
                  <p className="text-xs font-semibold text-slate-500 mb-1 flex items-center gap-1">
                    <Target size={11} /> 학습 목표
                  </p>
                  <p className="text-sm text-slate-700 leading-relaxed">{selected.objective}</p>
                </div>
                {selected.teaches.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {selected.teaches.map(c => (
                      <span key={c} className={`text-xs px-2 py-0.5 rounded-full font-mono font-medium ${selected.color.badge}`}>
                        {c}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* ── 스토리텔링 인트로 ── */}
              {STEP_STORIES[selected.id] && (
                <StoryCard stepId={selected.id} color={selected.color} />
              )}

              {/* Concept */}
              <div className="card">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-slate-800 flex items-center gap-2">
                    <Lightbulb size={16} className="text-yellow-500" />
                    개념 설명
                  </h3>
                  {/* 파편 학습 — 영상 복습 버튼 (3단계 UI) */}
                  {(() => {
                    const approved   = getApprovedByTag(selected.id)
                    const recruiting = getRecruitingByTag(selected.id)
                    if (approved.length > 0) {
                      return (
                        <button
                          onClick={() => setVideoModal({ stepId: selected.id, label: selected.title })}
                          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl font-bold transition-all bg-red-500 hover:bg-red-600 text-white shadow-sm hover:shadow-md"
                          title={`승인된 영상 ${approved.length}개 있음`}
                        >
                          <Youtube size={12} />
                          영상으로 학습 ({approved.length})
                        </button>
                      )
                    }
                    if (recruiting.length > 0) {
                      return (
                        <button
                          onClick={() => setVideoModal({ stepId: selected.id, label: selected.title })}
                          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl font-bold transition-all bg-violet-100 hover:bg-violet-200 text-violet-700 border border-violet-300 hover:border-violet-400"
                          title="영상 크리에이터 모집 중"
                        >
                          <Youtube size={12} />
                          🎬 영상 모집 중
                        </button>
                      )
                    }
                    return (
                      <button
                        onClick={() => setVideoModal({ stepId: selected.id, label: selected.title })}
                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl font-bold transition-all bg-slate-100 hover:bg-slate-200 text-slate-400 border border-slate-200"
                        title="영상 없음"
                      >
                        <Youtube size={12} />
                        영상 학습
                      </button>
                    )
                  })()}
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <pre className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap font-sans">
                    {selected.concept}
                  </pre>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {selected.keyPoints.map(p => (
                    <span key={p} className="text-xs bg-primary-100 text-primary-700 px-2.5 py-1 rounded-full font-medium flex items-center gap-1">
                      <Star size={9} /> {p}
                    </span>
                  ))}
                </div>
              </div>

              {/* Examples */}
              <div className="card">
                <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                  <Calculator size={16} className="text-blue-500" />
                  예시 문제
                </h3>
                <div className="space-y-3">
                  {selected.examples.map((ex, i) => (
                    <ExampleCard key={i} ex={ex} idx={i} />
                  ))}
                </div>
              </div>

              {/* Practice */}
              <PracticeBox
                practice={selected.practice}
                stepId={selected.id}
                unitId={UNIT_ID}
                alreadyAnswered={isStepPracticed(selected.id)}
                onAnswered={() => {}}
              />

              {/* Misconceptions */}
              {selected.misconceptions.length > 0 && (
                <div className="rounded-xl border-2 border-red-200 bg-red-50 p-4">
                  <h3 className="font-bold text-red-700 text-sm mb-3 flex items-center gap-2">
                    <AlertTriangle size={14} /> 자주 하는 실수 (오개념 주의!)
                  </h3>
                  <div className="space-y-2">
                    {selected.misconceptions.map(m => (
                      <div key={m.code} className="flex items-start gap-2 bg-white rounded-lg px-3 py-2 border border-red-100">
                        <span className="text-xs font-black text-red-500 bg-red-100 px-1.5 py-0.5 rounded flex-shrink-0">{m.code}</span>
                        <span className="text-xs text-slate-700">{m.desc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 단계 완료 버튼 */}
              {!isStepCompleted(selected.id) ? (
                <button
                  onClick={() => handleComplete(selected.id)}
                  className={`w-full py-3 rounded-2xl text-white font-black text-sm flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg ${selected.color.btn}`}
                >
                  <CheckCircle size={16} /> 이 단계 학습 완료!
                </button>
              ) : (
                <div className="bg-emerald-50 border-2 border-emerald-300 rounded-2xl p-3 flex items-center gap-3">
                  <CheckCircle size={20} className="text-emerald-500 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-black text-emerald-700">완료된 단계입니다 🎉</p>
                    <p className="text-xs text-emerald-500">완료일: {stepProgress[selected.id]?.completedAt?.slice(0,10) ?? ''}</p>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex justify-between items-center pt-2">
                {selectedIdx > 0 ? (
                  <button
                    onClick={() => setSelectedId(STEPS[selectedIdx - 1].id)}
                    className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 transition-colors"
                  >
                    ← 이전 단계
                  </button>
                ) : <span />}
                {selectedIdx < STEPS.length - 1 ? (
                  <button
                    onClick={() => setSelectedId(STEPS[selectedIdx + 1].id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-semibold transition-all ${selected.color.btn}`}
                  >
                    다음 단계 <ArrowRight size={14} />
                  </button>
                ) : (
                  <span className="text-sm font-bold text-emerald-600 flex items-center gap-1">
                    <CheckCircle size={14} /> 전 과정 완료!
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 파편 학습 영상 모달 */}
      {videoModal && (
        <ConceptVideoModal
          conceptTag={videoModal.stepId}
          conceptLabel={videoModal.label}
          viewerId="student_001"
          onClose={() => setVideoModal(null)}
          onCompleted={() => setVideoModal(null)}
        />
      )}
    </div>
  )
}
